import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PackageDetailsBasicInformationTab from '../components/package_details/PackageDetailBasicInformationTab';
import PurchaseOrderEntriesTab from '../components/package_details/PurchaseOrderEntriesTab';
import PurchaseOrderTrackingLogsTab from '../components/package_details/PurchaseOrderTrackingLogsTab';
import PurchaseOrderTrackingTab from '../components/package_details/PurchaseOrderTrackingTab';
import purchaseOrdersService from '../services/purchase-orders-service';

const formatDateTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', // EST
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short' // "EST"
  }).format(date);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

export default function PurchaseOrderDetailsPage() {
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);

  // Tracking tab state
  const [trackingRecords, setTrackingRecords] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  // OData tracking state
  const [odataRecords, setOdataRecords] = useState(null);
  const [odataLoading, setOdataLoading] = useState(false);
  const [odataSearched, setOdataSearched] = useState(false);
  const [odataOrderNumber, setOdataOrderNumber] = useState(null);

  // Tracking logs tab state
  const [trackingLogs, setTrackingLogs] = useState(null);
  const [trackingLogsLoading, setTrackingLogsLoading] = useState(false);

  // PO Entries tab state
  const [poEntries, setPoEntries] = useState(null);
  const [poEntriesLoading, setPoEntriesLoading] = useState(false);

  // Modal state
  const [openEditModal, setOpenEditModal] = useState(false);
  // Surfaced inside the dialog. Failures were previously console.error only,
  // so a rejected save left the modal open with no explanation at all.
  const [saveError, setSaveError] = useState(null);
  const [formValues, setFormValues] = useState({});

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenEdit = () => {
    setFormValues(purchaseOrderDetails); // preload current values
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await purchaseOrdersService.updatePurchaseOrder(
        id,
        formValues
      );
      if (response.success) {
        setPurchaseOrderDetails(formValues);
        setSaveError(null);
        setOpenEditModal(false);
      } else {
        setSaveError(response.message || 'Failed to save changes.');
        console.error('Update error:', response.message);
      }
    } catch (error) {
      setSaveError(`Failed to save the purchase order. Please try again.`);
      console.error('Save error:', error);
    }
  };

  const handleSearchOdata = async () => {
    setOdataLoading(true);
    try {
      const response = await purchaseOrdersService.getTrackingByPOFromOdata(id);
      if (response.success) {
        setOdataRecords(response.data);
        setOdataOrderNumber(response.orderNumber || null);
      } else {
        setOdataRecords([]);
      }
    } catch (error) {
      console.error('Fetch OData tracking error:', error);
      setOdataRecords([]);
    } finally {
      setOdataLoading(false);
      setOdataSearched(true);
    }
  };

  useEffect(() => {
    const fetchPurchaseOrderDetails = async () => {
      try {
        const response = await purchaseOrdersService.getPurchaseOrder(id);
        if (response.success) {
          setPurchaseOrderDetails(response.data);
        } else {
          console.error('Error:', response.message);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrderDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab === 1 && trackingRecords === null) {
      const fetchTracking = async () => {
        setTrackingLoading(true);
        try {
          const response = await purchaseOrdersService.getTrackingByPO(id);
          if (response.success) {
            setTrackingRecords(response.data);
          } else {
            setTrackingRecords([]);
          }
        } catch (error) {
          console.error('Fetch tracking error:', error);
          setTrackingRecords([]);
        } finally {
          setTrackingLoading(false);
        }
      };
      fetchTracking();
    }
  }, [activeTab, id, trackingRecords]);

  useEffect(() => {
    if (activeTab === 2 && trackingLogs === null) {
      const fetchTrackingLogs = async () => {
        setTrackingLogsLoading(true);
        try {
          const response = await purchaseOrdersService.getTrackingLogsByPO(id);
          if (response.success) {
            setTrackingLogs(response.data);
          } else {
            setTrackingLogs([]);
          }
        } catch (error) {
          console.error('Fetch tracking logs error:', error);
          setTrackingLogs([]);
        } finally {
          setTrackingLogsLoading(false);
        }
      };
      fetchTrackingLogs();
    }
  }, [activeTab, id, trackingLogs]);

  useEffect(() => {
    if (activeTab === 3 && poEntries === null) {
      const fetchEntries = async () => {
        setPoEntriesLoading(true);
        try {
          const response =
            await purchaseOrdersService.getPurchaseOrderEntries(id);
          if (response.success) {
            setPoEntries(response.data);
          } else {
            setPoEntries([]);
          }
        } catch (error) {
          console.error('Fetch PO entries error:', error);
          setPoEntries([]);
        } finally {
          setPoEntriesLoading(false);
        }
      };
      fetchEntries();
    }
  }, [activeTab, id, poEntries]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  const overviewItems = [
    { title: 'ID', value: purchaseOrderDetails.ID },
    { title: 'PO Number', value: purchaseOrderDetails.PONumber },
    { title: 'PO Title', value: purchaseOrderDetails.POTitle },
    { title: 'PO Type', value: purchaseOrderDetails.POType },
    { title: 'Status', value: purchaseOrderDetails.Status },
    {
      title: 'Date Created',
      value: formatDateTime(purchaseOrderDetails.DateCreated)
    },
    {
      title: 'Last Updated',
      value: formatDateTime(purchaseOrderDetails.LastUpdated)
    },
    {
      title: 'Date Placed',
      value: formatDateTime(purchaseOrderDetails.DatePlaced)
    },
    {
      title: 'Required Date',
      value: formatDateTime(purchaseOrderDetails.RequiredDate)
    },
    {
      title: 'Is Placed',
      value: purchaseOrderDetails.IsPlaced ? 'Yes' : 'No'
    },
    { title: 'Supplier ID', value: purchaseOrderDetails.SupplierID },
    { title: 'Ship Via', value: purchaseOrderDetails.ShipVia },
    { title: 'To', value: purchaseOrderDetails.To },
    { title: 'Ship To', value: purchaseOrderDetails.ShipTo },
    { title: 'Requisitioner', value: purchaseOrderDetails.Requisitioner },
    { title: 'FOB Point', value: purchaseOrderDetails.FOBPoint },
    { title: 'Terms', value: purchaseOrderDetails.Terms },
    {
      title: 'Tax Rate',
      value: purchaseOrderDetails.TaxRate
        ? `${purchaseOrderDetails.TaxRate}%`
        : ''
    },
    {
      title: 'Shipping',
      value: formatCurrency(purchaseOrderDetails.Shipping)
    },
    { title: 'Freight', value: purchaseOrderDetails.Freight },
    {
      title: 'Estimated Shipping',
      value: formatCurrency(purchaseOrderDetails.EstShipping)
    },
    {
      title: 'Current Shipping',
      value: formatCurrency(purchaseOrderDetails.CurrentShipping)
    },
    {
      title: 'Estimated Other Fees',
      value: formatCurrency(purchaseOrderDetails.EstOtherFees)
    },
    {
      title: 'Current Other Fees',
      value: formatCurrency(purchaseOrderDetails.CurrentOtherFees)
    },
    {
      title: 'Other Fees',
      value: formatCurrency(purchaseOrderDetails.OtherFees)
    },
    { title: 'Confirming To', value: purchaseOrderDetails.ConfirmingTo },
    { title: 'Remarks', value: purchaseOrderDetails.Remarks },
    { title: 'Store ID', value: purchaseOrderDetails.StoreID },
    { title: 'Worksheet ID', value: purchaseOrderDetails.WorksheetID },
    { title: 'Other Store ID', value: purchaseOrderDetails.OtherStoreID },
    { title: 'Currency ID', value: purchaseOrderDetails.CurrencyID },
    {
      title: 'Exchange Rate',
      value: purchaseOrderDetails.ExchangeRate
    },
    { title: 'Other PO ID', value: purchaseOrderDetails.OtherPOID },
    {
      title: 'Inventory Location',
      value: purchaseOrderDetails.InventoryLocation
    },
    { title: 'Batch Number', value: purchaseOrderDetails.BatchNumber },
    {
      title: 'Cost Distribution Method',
      value: purchaseOrderDetails.CostDistributionMethod
    },
    { title: 'Parent PO Id', value: purchaseOrderDetails.ParentPOId },
    { title: 'Root PO Id', value: purchaseOrderDetails.RootPOId },
    { title: 'Origin PO Id', value: purchaseOrderDetails.OriginPOId },
    { title: 'Master PO', value: purchaseOrderDetails.MasterPO },
    {
      title: 'Discrepancy Status',
      value: purchaseOrderDetails.DiscrepancyStatus
    },
    { title: 'DB TimeStamp', value: purchaseOrderDetails.DBTimeStamp }
  ];

  return (
    <div>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: '#0F1117',
          minHeight: '100vh'
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Purchase Order Details
        </Typography>

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
              sx={{ backgroundColor: 'background.paper', flex: 1 }}
            >
              <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
              <Tab
                label="Tracking"
                icon={<LocalShippingIcon />}
                iconPosition="start"
              />
              <Tab
                label="Tracking Logs"
                icon={<ListAltIcon />}
                iconPosition="start"
              />
              <Tab
                label="PO Entries"
                icon={<ShoppingCartIcon />}
                iconPosition="start"
              />
            </Tabs>

            {/* Edit Button */}
            {activeTab === 0 && (
              <IconButton
                onClick={handleOpenEdit}
                sx={{ mr: 2 }}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
            {activeTab === 0 && (
              <PackageDetailsBasicInformationTab
                id={id}
                overviewItems={overviewItems}
                collapsibleItems={[]}
              />
            )}
            {activeTab === 1 &&
              (trackingLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <PurchaseOrderTrackingTab
                  trackingRecords={trackingRecords || []}
                  odataRecords={odataRecords}
                  odataLoading={odataLoading}
                  odataSearched={odataSearched}
                  odataOrderNumber={odataOrderNumber}
                  onSearchOdata={handleSearchOdata}
                />
              ))}
            {activeTab === 2 &&
              (trackingLogsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <PurchaseOrderTrackingLogsTab
                  trackingLogs={trackingLogs || []}
                />
              ))}
            {activeTab === 3 &&
              (poEntriesLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <PurchaseOrderEntriesTab poRecords={poEntries || []} />
              ))}
          </Box>
        </Paper>
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Purchase Order Details</DialogTitle>
        <DialogContent dividers>
          {/* Inside DialogContent deliberately. ZoeOrderDetailsPage renders its
              equivalent Alert on the page BEHIND the modal, where the user
              cannot see it while the dialog is open. */}
          {saveError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSaveError(null)}
            >
              {saveError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.keys(formValues).map((key) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={key}
                  name={key}
                  value={formValues[key] ?? ''}
                  onChange={handleInputChange}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
